import { Link } from "react-router-dom";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { PermissionCheck } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { PermissionRoles } from "@/enum";
import { useResumeWebhookMutation } from "@/pages/Private/redux/webhook/webhook.api";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";
import { useSnackBar } from "@/utils/useSnackbar";

import { Webhook } from "../schema/webhook";
import {
	describeWebhookHealth,
	WebhookHealthDescription,
	WebhookHealthTone,
} from "../utils/describeWebhookHealth";

const TONE_CLASSES: Record<WebhookHealthTone, string> = {
	error: "bg-status-error-light text-status-error",
	warning: "bg-status-warning-light text-text",
	ok: "bg-status-success-light text-status-success",
	idle: "bg-status-info-light text-status-info",
};

type Translate = (key: string, params?: Record<string, string>) => string;

/** The first line under the badge: what the receiver answered and since when. */
function webhookHealthProblem(health: WebhookHealthDescription, t: Translate): string | null {
	switch (health.state) {
		case "paused":
		case "failing":
			if (health.error && health.since) {
				return t("webhookHealth.errorSince", { error: health.error, since: health.since });
			}

			return health.since ? t("webhookHealth.since", { since: health.since }) : health.error;
		case "lastFailed":
		case "retrying":
			return health.error;
		default:
			return null;
	}
}

/**
 * The lines under the badge: the receiver's problem, then when it last accepted a lead, and while
 * delivering, when it last failed (PREC-4253). "No deliveries yet" has none.
 */
export function webhookHealthDetail(health: WebhookHealthDescription, t: Translate): string[] {
	if (health.state === "idle") {
		return [];
	}

	const lines = [webhookHealthProblem(health, t)];

	if (health.lastDelivered) {
		lines.push(t("webhookHealth.lastDelivered", { date: health.lastDelivered }));
	}

	if (health.state === "delivering" && health.lastFailed) {
		lines.push(t("webhookHealth.lastFailed", { date: health.lastFailed }));
	}

	return lines.filter((line): line is string => !!line);
}

/**
 * Whether a webhook's leads are arriving (PREC-4248). Until now the page had no way to say so: a
 * receiver that answered 404 for weeks looked exactly like one that worked.
 */
export const WebhookHealthCell: FunctionComponent<{ webhook: Webhook }> = ({ webhook }) => {
	const { t } = useTranslation();
	const [resumeWebhook, { isLoading }] = useResumeWebhookMutation();
	const { handleOpenSnackbar, renderMessageSnackbar } = useSnackBar();
	const health = describeWebhookHealth(webhook);
	const details = webhookHealthDetail(health, t);
	const givenUp = webhook.givenUpDeliveries ?? 0;

	const handleResume = async () => {
		try {
			await resumeWebhook(webhook.id).unwrap();
		} catch {
			// A refused mutation reaches the user through nothing else (root CLAUDE.md, PREC-4129).
			handleOpenSnackbar(t("webhookHealth.resumeFailed"));
		}
	};

	return (
		<div className="flex flex-col items-start gap-1" data-test="webhook-health">
			<span
				className={classNames(
					"rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap",
					TONE_CLASSES[health.tone]
				)}
			>
				{t(`webhookHealth.state.${health.state}`)}
			</span>
			{details.map((line) => (
				<span key={line} className="text-xs text-text-meta" data-test="webhook-health-line">
					{line}
				</span>
			))}
			{/* A webhook that never delivered has nothing to list (mockup frame 1, PREC-4250). */}
			{(health.state !== "idle" || givenUp > 0) && (
				<Link
					className="cursor-pointer text-xs text-brand-light hover:underline"
					to={ROUTE_CONFIG.INTEGRATIONS_WEBHOOK_DELIVERIES.replace(
						":webhookId",
						String(webhook.id)
					)}
				>
					{t("webhookHealth.deliveries.view")}
					{givenUp > 0 && (
						<span className="text-status-error">
							{" "}
							{t("webhookHealth.deliveries.givenUpCount", { count: givenUp })}
						</span>
					)}
				</Link>
			)}
			{health.canResume && (
				<PermissionCheck requiredPermissions={[PermissionRoles.WEBHOOK]}>
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						isLoading={isLoading}
						size={ButtonSize.XS}
						testId="webhook-resume"
						title={t("webhookHealth.resume")}
						type="button"
						onClick={handleResume}
					/>
				</PermissionCheck>
			)}
			{renderMessageSnackbar()}
		</div>
	);
};
